// tslint:disable:max-line-length

import { Plugin } from 'mggp-player';
import { EpgChannel, ITypeChannelsCollection } from './EpgChannel';
import { EpgChannelGenre } from './EpgChannelGenre';
import { EpgProgram } from './EpgProgram';
import { ChannelType, Consts, IEvent, IPlayer } from './index';
import { Model } from './Model';
import { Observer } from './Observer';
import { IChannelGroup } from './ChannelGroup';
import { PropertyHolder } from './PropertyHolder';
import { Utils } from './Utils';
import List from "./lib/List";
import { navigableNode } from "./lib/navigableNode";

export default class Epg extends Plugin implements IPlayer {
  public version = process.env.VERSION;
  public build = process.env.BUILD;
  public name = process.env.NAME;
  public FAVORITE_GENRE_ID = EpgChannelGenre.FAVORITE_GENRE_ID;
  public FEATURED_GENRE_ID = EpgChannelGenre.FEATURED_GENRE_ID;
  public Consts = Consts;
  public on: (eventName: string, cb: (arg0: any) => void) => void;
  public off: (eventName: string, cb: (arg0: any) => void) => void;
  public emit: (eventName: string, data: any) => void;
  public set: (name: string, data: any) => void;
  public get: (name: string) => any;
  public registerProperty: (
    propertyName: string,
    defaultValue: any,
    resetable?: boolean,
  ) => void;
  public reset: () => void;
  public get orderedChannels(){
    return this._model.orderedChannels;
  }
  public get channels(){
    return this._model.channels;
  }

  private readonly _model = new Model();
  private readonly player: IPlayer;

  constructor(player: IPlayer, ...rest: any) {
    super(player, ...rest);

    this.player = player;
    PropertyHolder.initialize(this.player);
    Observer.initialize(this.player);
    Utils.player = this.player;

    this.on = this.player.on;
    this.off = this.player.off;
    this.emit = this.player.emit;
    this.get = this.player.get;
    this.set = this.player.set;
    this.registerProperty = this.player.registerProperty;
    this.reset = this.player.reset;

    const options = PropertyHolder.get(Consts.PROPERTY_INIT_OPTIONS);
    if (options) {
      this.onInit({ name: '', data: options });
    } else {
      Observer.on(`property:${Consts.PROPERTY_INIT_OPTIONS}`, this.onInit);
    }
  }

  public dispose() {
    this._model.dispose();
  }

  public getOrderedChannels(): List<IChannelGroup> {
    return this._model.orderedChannels;
  }

  public getChannels(): ITypeChannelsCollection {
    return this._model.channels;
  }

  public getAllChannelsUrl(): string {
    return this._model.allChannelsUrl;
  }

  public getShowAllChannelsButton(): boolean {
    return this._model.showAllChannelsButton;
  }

  public translate(key: string, defaultValue: string): string {
    return Utils.translate(key, defaultValue);
  }

  public switchToProgram = (program: EpgProgram) => {
    let args: any[];
    const {
      channel: { type: channelType },
    } = program;
    if (channelType === ChannelType.DVR_CHANNEL && program.state === 1) {
      // if dvr channel and click on program that is on air
      args = [`${channelType}:backOnAir`];
    } else if (channelType === ChannelType.REGULAR_CHANNEL) {
      // if regular channel has some program to play
      // emit event to vodchannel plugin
      args = [`vodChannel:playProgram`, program];
    } else {
      // in this case we are on interactive channel, so just emit the event
      args = [`${channelType}:playProgram`, program];
    }
    this.emit.apply(this, args);
  }

  public switchToChannel = (id: EpgChannel['id'], groupId: number) => {
    this._model.loadGroupPrograms(groupId);
    this._model._activeGroupId = groupId;
    this.emit(Consts.EVENT_CHANNEL_SWITCH, { id });
  }

  public switchToNextChannel = () => {
    const activeGroup = this.activeGroup;
    const activeChannel = navigableNode<EpgChannel['id']>(this.activeChannel!, activeGroup.channels);

    if (activeChannel.hasNext) {
      this.switchToChannel(activeChannel.next, activeGroup.id);
    } else {
      const targetGroup = this._getNextNonEmptyGroup(activeGroup);
      this.switchToChannel(targetGroup.channels.head, targetGroup.id);
    }
  }

  public switchToPrevChannel = () => {
    const activeGroup = this.activeGroup;
    const activeChannel = navigableNode<EpgChannel['id']>(this.activeChannel!, activeGroup.channels);

    if (activeChannel.hasPrev) {
      this.switchToChannel(activeChannel.prev, activeGroup.id);
    } else {
      const targetGroup = this._getPrevNonEmptyGroup(activeGroup);
      this.switchToChannel(targetGroup.channels.tail, targetGroup.id);
    }
  }

  private _getNextNonEmptyGroup(_group_: any): IChannelGroup {
    const group = navigableNode<IChannelGroup>(_group_, this.orderedChannels);
    const next = group.hasNext ? group.next : this._model.orderedChannels.head;

    return next.channels.isEmpty ? this._getNextNonEmptyGroup(next) : next;
  }

  private _getPrevNonEmptyGroup(_group_: any): IChannelGroup {
    const group = navigableNode<IChannelGroup>(_group_, this.orderedChannels);
    const prev = group.hasPrev ? group.prev : this._model.orderedChannels.tail;

    return prev.channels.isEmpty ? this._getPrevNonEmptyGroup(prev) : prev;
  }

  public get activeGroup(): IChannelGroup {
    const {
      orderedChannels: channelGroups,
    } = this._model;

    if (null === this._model._activeGroupId){
      this._model._activeGroupId = this._model.resolveActiveGroup();
    }

    const group = channelGroups.find((group: any) => group.id === this._model._activeGroupId);
    if (group === void 0){
      throw new Error('Unresolved active channel group');
    }

    return group;
  }

  public get activeChannel(): EpgChannel['id'] | undefined {
    return this.activeGroup && this.activeGroup.channels.find((channelId) => channelId === this._model._currentChannelId);
  }

  private onInit = (event: IEvent) => {
    Observer.off(`property:${Consts.PROPERTY_INIT_OPTIONS}`, this.onInit);
    this._model.start(event.data);
    Observer.emit(Consts.EVENT_INITED, this);
  }
}
